import { useEffect } from 'react'

const SupportRequestQuestion = ({ formData, setFormData, questionState, setQuestionState, validationErrors = [] }) => {

    useEffect(() => {
        const existingResponses = formData.form_responses?.question1?.responses || [];
        if (existingResponses.length > 0) {
            const newState = { ...questionState };

            existingResponses.forEach(response => {
                if (newState[response.answer_text]) {
                    newState[response.answer_text].selected = true;

                    if (response.sub_responses && response.sub_responses.length > 0) {
                        response.sub_responses.forEach(subResponse => {
                            if (newState[response.answer_text].subOptions && newState[response.answer_text].subOptions[subResponse.answer_text]) {
                                newState[response.answer_text].subOptions[subResponse.answer_text].selected = true;

                                if (subResponse.duration) {
                                    newState[response.answer_text].subOptions[subResponse.answer_text].duration = subResponse.duration;
                                }
                            }
                        });
                    }

                    if (response.text_response) {
                        newState[response.answer_text].text_response = response.text_response;
                    }
                }
            });

            setQuestionState(newState);
        }
    }, [formData.form_responses?.question1?.responses])

    const uncheckSubOptions = (options) => {
        if (!options || typeof options !== 'object') return options;

        const updatedOptions = { ...options };
        Object.keys(updatedOptions).forEach(key => {
            if (updatedOptions[key] && typeof updatedOptions[key] === 'object') {
                updatedOptions[key] = {
                    ...updatedOptions[key],
                    selected: false
                };
                if (updatedOptions[key].subOptions) {
                    updatedOptions[key].subOptions = uncheckSubOptions(updatedOptions[key].subOptions);
                }
            }
        });
        return updatedOptions;
    };

    const handleQuestionUpdate = (responseKey, checkedValue, parentKey = null, dataKey = null, value = null) => {
        setQuestionState(prevState => {
            const newState = { ...prevState };

            if (dataKey) {
                if (parentKey) {
                    newState[parentKey] = {
                        ...newState[parentKey],
                        subOptions: {
                            ...newState[parentKey].subOptions,
                            [responseKey]: {
                                ...newState[parentKey].subOptions[responseKey],
                                [dataKey]: value
                            }
                        }
                    };
                } else {
                    newState[responseKey] = {
                        ...newState[responseKey],
                        [dataKey]: value
                    };
                }
            } else if (parentKey) {
                newState[parentKey] = {
                    ...newState[parentKey],
                    subOptions: {
                        ...newState[parentKey].subOptions,
                        [responseKey]: {
                            ...newState[parentKey].subOptions[responseKey],
                            selected: checkedValue
                        }
                    }
                };
            } else {
                newState[responseKey] = {
                    ...newState[responseKey],
                    selected: checkedValue
                };

                if (!checkedValue && newState[responseKey].subOptions) {
                    newState[responseKey].subOptions = uncheckSubOptions(newState[responseKey].subOptions);
                }
            }

            return newState;
        });

        setFormData(prevFormData => {
            const currentResponses = prevFormData.form_responses.question1?.responses || [];

            if (dataKey) {
                const updatedResponses = currentResponses.map(response => {
                    if (parentKey && response.answer_text === parentKey) {
                        return {
                            ...response,
                            sub_responses: response.sub_responses.map(subResponse =>
                                subResponse.answer_text === responseKey
                                    ? { ...subResponse, [dataKey]: value }
                                    : subResponse
                            )
                        };
                    } else if (!parentKey && response.answer_text === responseKey) {
                        return { ...response, [dataKey]: value };
                    }
                    return response;
                });

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question1: {
                            question_title: "Why is support being requested at this time?",
                            responses: updatedResponses
                        }
                    }
                };
            } else {
                let updatedResponses = [...currentResponses];

                if (parentKey) {
                    const parentResponseIndex = updatedResponses.findIndex(r => r.answer_text === parentKey);

                    if (parentResponseIndex !== -1) {
                        const parentResponse = updatedResponses[parentResponseIndex];
                        let updatedSubResponses = [...parentResponse.sub_responses];

                        if (checkedValue) {
                            if (!updatedSubResponses.find(sr => sr.answer_text === responseKey)) {
                                updatedSubResponses.push({
                                    answer_text: responseKey,
                                    sub_responses: []
                                });
                            }
                        } else {
                            updatedSubResponses = updatedSubResponses.filter(sr => sr.answer_text !== responseKey);
                        }

                        updatedResponses[parentResponseIndex] = {
                            ...parentResponse,
                            sub_responses: updatedSubResponses
                        };
                    }
                } else {
                    if (checkedValue) {
                        if (!updatedResponses.find(r => r.answer_text === responseKey)) {
                            updatedResponses.push({
                                answer_text: responseKey,
                                sub_responses: []
                            });
                        }
                    } else {
                        updatedResponses = updatedResponses.filter(r => r.answer_text !== responseKey);
                    }
                }

                return {
                    ...prevFormData,
                    form_responses: {
                        ...prevFormData.form_responses,
                        question1: {
                            question_title: "Why is support being requested at this time?",
                            responses: updatedResponses
                        }
                    }
                };
            }
        });
    };

    const hasResponse = (responseKey) => {
        return questionState[responseKey]?.selected || false;
    };

    const hasSubResponse = (parentKey, subKey) => {
        return questionState[parentKey]?.subOptions?.[subKey]?.selected || false;
    };

    const getResponseData = (responseKey, dataKey, parentKey = null) => {
        if (parentKey) {
            return questionState[parentKey]?.subOptions?.[responseKey]?.[dataKey] || '';
        }
        return questionState[responseKey]?.[dataKey] || '';
    };

    return (
        <div className="mt-6 border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-t-lg">
                <h2 className="text-lg font-semibold text-gray-800">
                    Step 2: Why is support being requested at this time?
                </h2>
                <span className="text-red-500 text-sm font-medium">Required *</span>
            </div>
            <div className={`overflow-hidden transition-all duration-700 ease-in-out`}>
                {validationErrors.includes('no_selection') && (
                    <div className="bg-red-50 border-l-4 border-red-400 p-3 mx-4 mt-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">
                                    Please select at least one option to continue.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <div className={`${validationErrors.includes('no_selection') ? 'border-2 border-red-200 bg-red-50 m-2 rounded-md' : ''}`}>
                    <div className="p-2">
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="trigger_policy"
                                        value="Policy/Attendance triggers"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Policy/Attendance triggers', e.target.checked)}
                                        checked={hasResponse('Policy/Attendance triggers')}
                                    />
                                    <label htmlFor="trigger_policy" className="text-sm text-gray-700">Policy/Attendance triggers</label>
                                </div>
                                {hasResponse('Policy/Attendance triggers') && (
                                    <div className="mt-2 ml-6 p-3 bg-gray-50 rounded-md">
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="short_absence"
                                                    value="Short term absence"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Short term absence', e.target.checked, 'Policy/Attendance triggers')}
                                                    checked={hasSubResponse('Policy/Attendance triggers', 'Short term absence')}
                                                />
                                                <label htmlFor="short_absence" className="text-sm text-gray-700">Short Term Absences</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="long_absence"
                                                    value="Long term absence"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Long term absence', e.target.checked, 'Policy/Attendance triggers')}
                                                    checked={hasSubResponse('Policy/Attendance triggers', 'Long term absence')}
                                                />
                                                <label htmlFor="long_absence" className="text-sm text-gray-700">Long Term Absences {hasSubResponse('Policy/Attendance triggers', 'Long term absence') && '(How Long?)'}</label>
                                            </div>
                                            {hasSubResponse('Policy/Attendance triggers', 'Long term absence') && (
                                                <div className="mt-2 ml-6">
                                                    <input
                                                        type="text"
                                                        id="longAbsenceDuration"
                                                        name="longAbsenceDuration"
                                                        className="border rounded-md py-1 px-2 w-48 text-sm"
                                                        placeholder="e.g. 6 weeks, 3 months"
                                                        onChange={(e) => handleQuestionUpdate('Long term absence', null, 'Policy/Attendance triggers', 'duration', e.target.value)}
                                                        value={getResponseData('Long term absence', 'duration', 'Policy/Attendance triggers')}
                                                    />
                                                </div>
                                            )}
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="frequent_absence"
                                                    value="Frequent intermittent absence"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Frequent intermittent absence', e.target.checked, 'Policy/Attendance triggers')}
                                                    checked={hasSubResponse('Policy/Attendance triggers', 'Frequent intermittent absence')}
                                                />
                                                <label htmlFor="frequent_absence" className="text-sm text-gray-700">Frequent Intermittent Absence</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="health_attendance"
                                                    value="Health issue impacting attendance"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Health issue impacting attendance', e.target.checked, 'Policy/Attendance triggers')}
                                                    checked={hasSubResponse('Policy/Attendance triggers', 'Health issue impacting attendance')}
                                                />
                                                <label htmlFor="health_attendance" className="text-sm text-gray-700">Health Issue Impacting Attendance</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="return_work"
                                                    value="Return to work after absence"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Return to work after absence', e.target.checked, 'Policy/Attendance triggers')}
                                                    checked={hasSubResponse('Policy/Attendance triggers', 'Return to work after absence')}
                                                />
                                                <label htmlFor="return_work" className="text-sm text-gray-700">Return To Work After Absence</label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="trigger_conduct"
                                        value="Conduct and Performance triggers"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Conduct and Performance triggers', e.target.checked)}
                                        checked={hasResponse('Conduct and Performance triggers')}
                                    />
                                    <label htmlFor="trigger_conduct" className="text-sm text-gray-700">Conduct and Performance triggers</label>
                                </div>
                                {hasResponse('Conduct and Performance triggers') && (
                                    <div className="mt-2 ml-6 p-3 bg-gray-50 rounded-md">
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="performance_concerns"
                                                    value="Performance concerns"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Performance concerns', e.target.checked, 'Conduct and Performance triggers')}
                                                    checked={hasSubResponse('Conduct and Performance triggers', 'Performance concerns')}
                                                />
                                                <label htmlFor="performance_concerns" className="text-sm text-gray-700">Performance Concerns</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="behavioral_concerns"
                                                    value="Behavioral concerns"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Behavioral concerns', e.target.checked, 'Conduct and Performance triggers')}
                                                    checked={hasSubResponse('Conduct and Performance triggers', 'Behavioral concerns')}
                                                />
                                                <label htmlFor="behavioral_concerns" className="text-sm text-gray-700">Behavioral Concerns</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="escalation_required"
                                                    value="Escalation required by HR or school policy"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Escalation required by HR or school policy', e.target.checked, 'Conduct and Performance triggers')}
                                                    checked={hasSubResponse('Conduct and Performance triggers', 'Escalation required by HR or school policy')}
                                                />
                                                <label htmlFor="escalation_required" className="text-sm text-gray-700">Escalation Required By HR Or School Policy</label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="trigger_health"
                                        value="Health and Safety Triggers"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Health and Safety Triggers', e.target.checked)}
                                        checked={hasResponse('Health and Safety Triggers')}
                                    />
                                    <label htmlFor="trigger_health" className="text-sm text-gray-700">Health and Safety Triggers</label>
                                </div>
                                {hasResponse('Health and Safety Triggers') && (
                                    <div className="mt-2 ml-6 p-3 bg-gray-50 rounded-md">
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id="safety_concern"
                                                    value="Health and safety concern following risk assessment"
                                                    className="mr-2"
                                                    onChange={(e) => handleQuestionUpdate('Health and safety concern following risk assessment', e.target.checked, 'Health and Safety Triggers')}
                                                    checked={hasSubResponse('Health and Safety Triggers', 'Health and safety concern following risk assessment')}
                                                />
                                                <label htmlFor="safety_concern" className="text-sm text-gray-700">Health And Safety Concern Following Risk Assessment</label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="trigger_general"
                                    value="General Advice"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('General Advice', e.target.checked)}
                                    checked={hasResponse('General Advice')}
                                />
                                <label htmlFor="trigger_general" className="text-sm text-gray-700">General Advice</label>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="trigger_hr_specialists"
                                    value="Would you like to discuss this with our in-house HR specialists?"
                                    className="mr-2"
                                    onChange={(e) => handleQuestionUpdate('Would you like to discuss this with our in-house HR specialists?', e.target.checked)}
                                    checked={hasResponse('Would you like to discuss this with our in-house HR specialists?')}
                                />
                                <label htmlFor="trigger_hr_specialists" className="text-sm text-gray-700">Would you like to discuss this with our in-house HR specialists?</label>
                            </div>

                            <div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="trigger_other"
                                        value="Other"
                                        className="mr-2"
                                        onChange={(e) => handleQuestionUpdate('Other', e.target.checked)}
                                        checked={hasResponse('Other')}
                                    />
                                    <label htmlFor="trigger_other" className="text-sm text-gray-700">Other Trigger (Please State)</label>
                                </div>
                                {hasResponse('Other') && (
                                    <div className="mt-2 ml-6">
                                        <textarea
                                            id="supportReasonOther"
                                            name="supportReasonOther"
                                            rows="2"
                                            className="border rounded-md py-2 px-4 w-full resize-vertical"
                                            placeholder="Please specify other trigger..."
                                            onChange={(e) => handleQuestionUpdate('Other', null, null, 'text_response', e.target.value)}
                                            value={getResponseData('Other', 'text_response')}
                                        ></textarea>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupportRequestQuestion;